import useModal from 'lib/hooks/useModal';
import { ia, cn, pluralize } from 'lib/helpers/utility';
import { RECEIPT_CHANNELS, isChannelAvailable } from 'lib/helpers/receiptDelivery';

import Button from 'components/shared/Button/Button';
import Popover from 'components/shared/Popovers/Popover/Popover';
import Typography from 'components/shared/Typography/Typography';

import CustomContactModal from './CustomContactModal';

// `channels` comes from the backend (practice email/text switches, patient SMS consent); a channel
// the backend would refuse is hidden. Unknown (undefined) shows both.
const DocumentDelivery = ({
  patient,
  channels,
  isDisabled,
  busy,
  onSend,
  selectedIds,
  areAllSelected
}) => {
  const canEmail = isChannelAvailable(channels, RECEIPT_CHANNELS.EMAIL);
  const canText = isChannelAvailable(channels, RECEIPT_CHANNELS.SMS);

  const { isOpen: customContactType, openModal, closeModal } = useModal();

  const emailOptions = [
    patient?.email && { label: patient.email, onClick: () => onSend('email') },
    patient?.alt_email && {
      label: patient.alt_email,
      onClick: () => onSend('email', { toAltEmail: true })
    },
    { label: 'Custom email address', onClick: () => openModal('email') }
  ].filter(Boolean);

  const phoneOptions = [
    patient?.phone && { label: patient.phone, onClick: () => onSend('text') },
    patient?.alt_phone && {
      label: patient.alt_phone,
      onClick: () => onSend('text', { toAltPhone: true })
    },
    { label: 'Custom phone number', onClick: () => openModal('phone') }
  ].filter(Boolean);

  const handleCustomSubmit = (type, value) =>
    type === 'email'
      ? onSend('email', { customEmail: value })
      : onSend('text', { customPhone: value });

  return (
    <div className="flex w-full justify-between gap-3 self-start sm:flex-col">
      <Typography
        variant="p"
        size="small"
        weight="medium"
        color="neutral-600"
        parentClassName="min-w-0"
        className="block">
        Send{' '}
        <span className="font-bold">
          {ia(selectedIds, 1) ? `${areAllSelected ? 'all' : ''} ${selectedIds.length}` : ''}
        </span>{' '}
        {pluralize('document', selectedIds.length)}
      </Typography>
      <div className="flex shrink-0 flex-nowrap items-center gap-3">
        {canEmail && (
          <Popover
            isFixed
            options={emailOptions}
            customButtonTrigger={
              <Button
                text="Email"
                variant="tertiary"
                size="small"
                icon="mail-mono"
                iconColor="neutral"
                iconShade={700}
                className={cn(isDisabled && 'bg-neutral-75')}
                isLoading={busy === 'email'}
                isDisabled={isDisabled}
              />
            }
          />
        )}
        {canText && (
          <Popover
            isFixed
            options={phoneOptions}
            customButtonTrigger={
              <Button
                text="Text"
                variant="tertiary"
                size="small"
                icon="message-mono"
                iconColor="neutral"
                iconShade={700}
                className={cn(isDisabled && 'bg-neutral-75')}
                isLoading={busy === 'text'}
                isDisabled={isDisabled}
              />
            }
          />
        )}
      </div>
      <CustomContactModal
        type={customContactType}
        onClose={closeModal}
        onSubmit={handleCustomSubmit}
        isLoading={!!busy}
      />
    </div>
  );
};

export default DocumentDelivery;
